import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bulkAPI } from '../services/api';
import { notify } from '../utils/notify';
import { HiCloudUpload, HiPause, HiCheck, HiX, HiRefresh, HiExclamation } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { PRODUCT_IMAGE_FALLBACK } from '../utils/imagePlaceholder';
import { useCarga } from '../hooks/useCarga';
import ErrorAlCargar from '../components/ErrorAlCargar';
import { mensajeDeError } from '../utils/apiError';


/**
 * Por qué falló, en palabras.
 *
 * `error_message` guarda un JSON —`[{ id, error }]`— y la página lo pintaba
 * tal cual: en pantalla salía `[{"id":"e","error":"..."}]`, que es exactamente
 * el defecto de enseñar datos del código. Se sacan los motivos, se juntan los
 * repetidos y se cuentan.
 *
 * Si el contenido no es ese JSON —un fallo de la tanda entera, por ejemplo— se
 * enseña tal cual: inventarle forma sería peor.
 */
function motivosDe(operation) {
    const crudo = operation.error_message;
    if (!crudo) return [];

    let lista;
    try {
        lista = JSON.parse(crudo);
    } catch {
        return [{ texto: crudo, veces: 1 }];
    }

    if (!Array.isArray(lista)) return [{ texto: crudo, veces: 1 }];

    const cuenta = new Map();
    for (const fallo of lista) {
        const texto = fallo?.error || String(fallo);
        cuenta.set(texto, (cuenta.get(texto) || 0) + 1);
    }

    return [...cuenta].map(([texto, veces]) => ({ texto, veces }));
}

/**
 * Los productos de una tanda, vengan en la forma que vengan.
 *
 * El servidor los devolvía como texto —solo el nombre— y pasó a devolver
 * `{ titulo, imagen }`. Las dos mitades no se despliegan a la vez: el navegador
 * recibe la página nueva en un minuto y el servidor tarda varios más, así que
 * durante ese rato la página nueva leía `p.titulo` de una cadena y pintaba
 * filas sin nombre y sin imagen.
 *
 * Cambiar la forma de un dato que la pantalla ya consume obliga a aceptar las
 * dos mientras dure el relevo. Esto lo normaliza en un solo sitio.
 */
function productosDe(operation) {
    return (operation.productos || []).map((p) =>
        typeof p === 'string' ? { titulo: p, imagen: null } : p
    );
}

/** Si la tanda todavía está en marcha. */
const enCurso = (o) => o.status === 'pending' || o.status === 'processing';

/** Los que no se tocaron porque no había nada que hacerles. */
const omitidosDe = (o) => Number(o.results?.omitidos) || 0;

/**
 * Qué hizo esta tanda, dicho en una frase.
 *
 * Sale de lo que la fila ya guardaba y la página tiraba: el tipo de operación,
 * el margen aplicado, el estado al que se cambió y cuántos productos.
 */
function resumenDeOperacion(o, t) {
    const n = o.total_items || 0;
    const datos = o.operation_data || {};

    switch (o.operation_type) {
        case 'bulk_import':
            return t('bulk_operations.summary_import', { count: n });

        case 'bulk_price_update':
            return t('bulk_operations.summary_price', {
                count: n,
                margen: datos.markupPercentage ?? '?'
            });

        case 'bulk_status_change':
            return datos.status === 'paused'
                ? t('bulk_operations.summary_withdraw', { count: n })
                : t('bulk_operations.summary_draft', { count: n });

        default:
            // Un tipo que no reconocemos se enseña tal cual en vez de
            // inventarle un nombre.
            return t('bulk_operations.summary_unknown', { tipo: o.operation_type, count: n });
    }
}

export default function BulkOperations() {
    const [operations, setOperations] = useState([]);
    const { cargando: loading, error, ejecutar, reintentar } = useCarga();
    const { t } = useTranslation();


    useEffect(() => {
        fetchOperations();
    }, []);

    /**
     * Mientras haya una tanda en marcha, se vuelve a preguntar.
     *
     * La página pintaba un contador de progreso vivo y un icono girando con
     * **una sola carga**: el número se quedaba congelado en lo que hubiera al
     * abrir, y la única forma de moverlo era pulsar el botón de recargar. Un
     * icono que gira junto a un número que no cambia dice que algo avanza
     * cuando lo que pasa es que nadie ha vuelto a mirar.
     *
     * Cada cinco segundos, y **solo** mientras quede algo en curso: con todo
     * terminado no hay nada que refrescar y preguntar sería gastar por gastar.
     * Se usa `bulkAPI` directamente y no `ejecutar` porque esto es recarga de
     * fondo: un fallo pasajero no debe borrar la lista ni sacar un aviso por
     * minuto, que es peor que el fallo.
     */
    useEffect(() => {
        if (!operations.some(enCurso)) return;

        const reloj = setInterval(async () => {
            try {
                const respuesta = await bulkAPI.getOperations();
                setOperations(respuesta.data.data.operations || []);
            } catch {
                // En segundo plano no se avisa. Lo que se vea seguirá siendo lo
                // último que sí se pudo leer.
            }
        }, 5000);

        return () => clearInterval(reloj);
    }, [operations]);

    const fetchOperations = () =>
        ejecutar(async () => {
            const response = await bulkAPI.getOperations();
            setOperations(response.data.data.operations || []);
        });

    const getStatusIcon = (status) => {
        switch (status) {
            // Una tanda con fallos NO lleva la marca verde de terminado.
            //
            // Estaban en el mismo `case`, así que «se importaron los 50» y «se
            // importaron 3 y fallaron 47» se leían igual de un vistazo. Es el
            // mismo defecto que la lista de anuncios documenta haber corregido:
            // un fallo no se pinta como un éxito.
            case 'completed_with_errors': return <HiExclamation className="w-4 h-4 text-warning-500" />;
            case 'completed': return <HiCheck className="w-4 h-4 text-green-500" />;
            case 'processing': return <HiRefresh className="w-4 h-4 text-blue-500 animate-spin" />;
            case 'failed': return <HiX className="w-4 h-4 text-red-500" />;
            case 'cancelled': return <HiPause className="w-4 h-4 text-yellow-500" />;
            default: return <HiCloudUpload className="w-4 h-4 text-slate-500" />;
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed': return 'badge-success';
            // Faltaba, asi que una tanda con fallos caia en el gris de
            // `default` --el color de «no se sabe»-- cuando si se sabe: fallo
            // parte de ella.
            case 'completed_with_errors': return 'badge-warning';
            case 'processing': return 'badge-info';
            case 'failed': return 'badge-danger';
            case 'cancelled': return 'badge-warning';
            default: return 'bg-slate-100 text-slate-600';
        }
    };

    const handleCancelOperation = async (operationId) => {
        try {
            await bulkAPI.cancelOperation(operationId);
            notify.success(t('bulk_operations.cancel_success'));
            fetchOperations();
        } catch (error) {
            notify.error(mensajeDeError(error, t('bulk_operations.cancel_failed')));
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="page-title flex items-center gap-2">
                    <HiCloudUpload className="w-8 h-8 text-blue-500" />
                    {t('bulk_operations.title')}
                </h1>
                <p className="text-slate-500 dark:text-slate-400 mt-1">
                    {t('bulk_operations.subtitle')}
                </p>
            </div>

            {/* Dónde se lanzan. Esta página es donde se sigue el
                progreso, no donde se empieza: marcar productos se hace en la
                lista donde están. */}
            <div className="card flex flex-wrap items-center gap-3 !py-3">
                <span className="text-sm text-slate-500">{t('bulk_operations.where_to_start')}</span>
                <Link to="/products/search" className="btn-secondary btn-sm">
                    {t('sidebar.product_search')}
                </Link>
                <Link to="/products/imported" className="btn-secondary btn-sm">
                    {t('sidebar.my_products')}
                </Link>
            </div>

            {/* Operations History */}
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">{t('bulk_operations.history_title')}</h2>
                    <button
                        onClick={fetchOperations}
                        className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                        <HiRefresh className="w-4 h-4" />
                    </button>
                </div>

                {loading ? (
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-16 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                        ))}
                    </div>
                ) : error ? (
                    <ErrorAlCargar error={error} onReintentar={reintentar} />
                ) : operations.length > 0 ? (
                    <div className="space-y-3">
                        {operations.map((operation) => (
                            <div key={operation.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        {/* Qué se hizo, en una frase.
                                            Aquí se pintaba `operation_type` con un
                                            replace encima —«bulk status_change»,
                                            el valor crudo de la base de datos— y
                                            tres números sin sustantivo. El margen
                                            que aplicaste y el estado al que
                                            cambiaste estaban guardados y no se
                                            enseñaban. */}
                                        <p className="text-slate-900 dark:text-white font-medium">
                                            {resumenDeOperacion(operation, t)}
                                        </p>

                                        {/* Con imagen, como en el resto de la
                                            aplicación: una miniatura centra la
                                            mirada en el producto mucho antes que
                                            su nombre. */}
                                        {productosDe(operation).length > 0 && (
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2">
                                                {productosDe(operation).map((p, i) => (
                                                    <div key={i} className="flex items-center gap-2 min-w-0 max-w-xs">
                                                        <img
                                                            src={p.imagen || PRODUCT_IMAGE_FALLBACK}
                                                            alt=""
                                                            className="w-10 h-10 rounded object-cover flex-shrink-0 bg-slate-100 dark:bg-slate-700"
                                                        />
                                                        <span className="text-sm text-slate-500 line-clamp-2">
                                                            {p.titulo}
                                                        </span>
                                                    </div>
                                                ))}
                                                {operation.productosRestantes > 0 && (
                                                    <span className="text-sm text-slate-500">
                                                        {t('bulk_operations.and_more', { count: operation.productosRestantes })}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* El resultado solo cuando no salió todo
                                            bien: repetir «2 de 2» es ruido. */}
                                        {(operation.failed_items > 0 || omitidosDe(operation) > 0 || enCurso(operation)) && (
                                            <div className="flex flex-wrap gap-3 mt-2 text-sm">
                                                <span className={`badge ${getStatusBadge(operation.status)} inline-flex items-center gap-1`}>
                                                    {getStatusIcon(operation.status)}
                                                    {t(`bulk_operations.status_${operation.status}`)}
                                                </span>
                                                {enCurso(operation) && (
                                                    <span className="text-slate-500">
                                                        {t('bulk_operations.progress', {
                                                            hechos: operation.processed_items,
                                                            total: operation.total_items
                                                        })}
                                                    </span>
                                                )}
                                                {operation.failed_items > 0 && (
                                                    <span className="text-red-500">
                                                        {t('bulk_operations.n_failed', { count: operation.failed_items })}
                                                    </span>
                                                )}
                                                {omitidosDe(operation) > 0 && (
                                                    <span className="text-amber-600 dark:text-amber-400">
                                                        {t('bulk_operations.n_skipped', { count: omitidosDe(operation) })}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {motivosDe(operation).length > 0 && (
                                            <ul className="mt-2 text-sm text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded space-y-1">
                                                {motivosDe(operation).map((m, i) => (
                                                    <li key={i}>
                                                        {m.veces > 1 ? `${m.texto} (${m.veces})` : m.texto}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <span className="text-xs text-slate-500">
                                            {new Date(operation.created_at).toLocaleString()}
                                        </span>
                                        {(operation.status === 'pending' || operation.status === 'processing') && (
                                            <button
                                                onClick={() => handleCancelOperation(operation.id)}
                                                className="text-sm text-red-500 hover:text-red-700"
                                            >
                                                {t('bulk_operations.cancel')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16">
                        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                            <HiCloudUpload className="w-8 h-8 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">{t('bulk_operations.no_operations_title')}</h3>
                        <p className="text-slate-500 max-w-md mx-auto">
                            {t('bulk_operations.no_operations_desc')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}